// WHO HOLDS WHAT IN THIS CUSTOMER, arranged the way an operator reads it: one entry per PERSON, with
// every role that person holds in this tenant beside them.
//
// WHY THIS IS A MODULE AND NOT A `reduce` INSIDE THE COMPONENT. `GET …/roles` answers one row per (person,
// role) PAIR — that is the shape ADR-0021 §8.2 reads out of `tenant_memberships` and out of the tenant's
// own people, and it is the right shape on the wire, because a role is what a grant writes and a removal
// names. It is the wrong shape on screen. The console rendered it row for row until this task, and the
// owner's report (30.10.2026) is what that cost: *"haltijoiden tiedot ja roolit eivät ole havainnollisia
// vaan epäselviä"* — the same consultant listed three times, once per code, with nothing saying it was one
// person. With 50–100+ customers per consultant (M7) and §3.3's five codes the repetition grows, so the
// grouping is not a cosmetic change and it is the one piece of this section that can be decided without a
// DOM. Hence here, tested by `tenant-role-holders.test.mjs` (ADR-0002 Layer 2 entry rule A — a decision
// over wire values, with more cases than a browser run would sample).
//
// IT IMPORTS NOTHING AT RUNTIME, deliberately: that is what lets Node's own test runner load it under
// `--experimental-strip-types`, the arrangement `tenantAccess.ts` already uses in this directory.

/**
 * The membership carrier, spelled here a SECOND time rather than imported from `tenantsApi.ts`'s
 * `CARRIER_MEMBERSHIP`.
 *
 * Importing it would pull the API client into this module and end the zero-runtime-import property the
 * header comment is about — which is the whole reason the carrier choice below can be decided and tested
 * without a DOM. The two spellings are pinned together by a test that reads the constant out of
 * `tenantsApi.ts`, so a change to either one fails rather than drifting — a drift would silently give
 * every membership the client role's sentence, which is the defect the functions below exist to remove.
 */
export const MEMBERSHIP_CARRIER = "membership";

/**
 * Which `tenants.roles.confirmBody*` string the REMOVAL'S CONFIRMATION gets, by the carrier of the chip
 * the operator clicked.
 *
 * WHY THE CONFIRMATION BRANCHES AND NOT ONLY THE BANNER. This is the decision point: the operator reads
 * this sentence to decide whether to click at all, which makes a wrong bound here worse than the same
 * bound in the banner afterwards. The two carriers take the access away at genuinely different moments:
 *
 *   * `membership` — a `tenant_memberships` row, read on every request with NO cache (ADR-0021 §4.3), so
 *     the access is gone as soon as the removal answers. A confirmation promising "at their next sign-in"
 *     invites the operator to think they still have time to change their mind, and tells a support desk
 *     the person keeps working until they sign out. Both are false, and the second one is the dangerous
 *     direction: it reads as "access ends later" when it has already ended.
 *   * `client_role` — a role in the bearer token (`TenantResolutionMiddleware`), honoured until the token
 *     is replaced. The next-sign-in sentence is correct here and keeps its wording.
 *
 * An unknown carrier string takes the TOKEN-bound sentence, which is this function's only real judgement
 * call. It is the safe direction of the two: promising a bound that is later than the truth leaves an
 * operator waiting for an end that has already happened, while promising one that is earlier would have
 * them tell somebody they are locked out of a customer they can still reach.
 */
export function revokeConfirmKey(carrier: string): string {
  return carrier === MEMBERSHIP_CARRIER
    ? "tenants.roles.confirmBodyMembership"
    : "tenants.roles.confirmBody";
}

/** One role a person holds in this tenant, with the carrier the server reported it on. */
export interface HeldRole {
  readonly role: string;
  /** `TenantRoleCarriers` — kept on the row, not shown as text (see `TenantRolesSection`). */
  readonly carrier: string;
}

/** One PERSON on the holder list, and every grantable role they hold in this tenant. */
export interface RoleHolderPerson {
  /** The realm's own id for the account — what a removal names, and this row's identity. */
  readonly subject: string;
  readonly email: string | null;
  readonly name: string | null;
  /**
   * The tenant this person's ACCOUNT belongs to, and what to call it — their HOME ORGANISATION
   * (`TenantRoleHolderDto.homeTenantId` / `homeTenantName`, ADR-0021 §8.2.1).
   *
   * BOTH, because the row shows the name and the decision to show it at all is made on the ID: the line
   * appears only when the home tenant is not the tenant being viewed, and comparing names would compare
   * two strings a tenant's own pääkäyttäjä can edit. Null for the client-role carrier (the home tenant IS
   * this tenant), for an account the realm would not answer for, and for one bound to nothing.
   */
  readonly homeTenantId: string | null;
  readonly homeTenantName: string | null;
  /**
   * The person as ONE string, for the places that have room for only one: the removal's confirmation and
   * the banner it writes afterwards. The list itself does not use it — a row has up to three lines.
   */
  readonly label: string;
  readonly roles: readonly HeldRole[];
}

/** The wire row, narrowed to what the grouping reads (`TenantRoleHolderDto`). */
export interface HolderRow {
  readonly subject: string;
  readonly email?: string | null;
  readonly name?: string | null;
  readonly homeTenantId?: string | null;
  readonly homeTenantName?: string | null;
  readonly role: string;
  readonly carrier: string;
}

/**
 * A holder as a human reads them: the realm's name and the address when there are both, otherwise
 * whichever one exists, and the raw subject when neither does.
 *
 * The subject fallback is not theoretical. A membership holder's name comes from the realm (§8.2), and a
 * realm that will not answer leaves the row NAMELESS rather than absent — the `tenant_memberships` row
 * says they hold the role, so dropping them would be a false claim about access where an unnamed row is
 * only an unhelpful one.
 */
export function holderLabel(holder: {
  readonly name?: string | null;
  readonly email?: string | null;
  readonly subject: string;
}): string {
  if (holder.name && holder.email) return `${holder.name} (${holder.email})`;
  return holder.name ?? holder.email ?? holder.subject;
}

/**
 * One entry per PERSON, keyed by the realm SUBJECT and not by the address.
 *
 * The subject is the identity a removal names (`RevokeTenantRoleRequest`) and the one field every row is
 * guaranteed to carry; an address is null for an account that has none, so grouping by it would fold two
 * such people into one row whose × buttons acted on whichever of them the server listed first.
 *
 * ORDER IS THE SERVER'S, by first appearance — the list has a stated order (`ListHoldersAsync` sorts the
 * memberships by role and then by subject, and the tenant's own people by `created_at`), and this function
 * is not the place to have an opinion about it. A person's roles keep the same rule, so the chips of a row
 * read in the order the holder list named them.
 *
 * THE NAME AND THE ADDRESS ARE THE FIRST NON-NULL ONES, not the first row's. The two carriers resolve them
 * from different places — the realm for a membership, this product's own decrypted `users` row for a
 * client role — so one of a person's rows can legitimately know what another does not, and a row that fell
 * back to the subject while a sibling carried the name would be the one case this grouping could make
 * worse than the list it replaced.
 *
 * THE HOME ORGANISATION FOLLOWS THE SAME RULE, and its id and name are kept TOGETHER rather than each
 * taken first-non-null on its own: the pair is one fact about one account (§8.2.1 — the id is the realm's
 * answer and the name is this database's), and a row whose id came from one carrier and whose name came
 * from another could show one tenant's name beside another tenant's id. So the first row that NAMES a home
 * tenant supplies both. A row that carries the id and NO name — a home tenant this product has no row for
 * — therefore contributes neither, which is the right answer for a surface that shows an organisation's
 * name: there is nothing it could draw for a bare uuid, and it must not block a later row that does name
 * one.
 *
 * A CODE IS LISTED ONCE. One role in one tenant has one carrier (§3.3), so a repeated code should not
 * reach here at all; if a migration or a restore produces one, two identical chips with two × buttons
 * acting on the same removal is a worse answer than one chip, and the carrier kept is the first listed —
 * the same choice `ListHoldersAsync` makes when it orders the two halves.
 */
export function groupHolders(rows: readonly HolderRow[]): RoleHolderPerson[] {
  const bySubject = new Map<
    string,
    {
      email: string | null;
      name: string | null;
      homeTenantId: string | null;
      homeTenantName: string | null;
      roles: HeldRole[];
    }
  >();

  for (const row of rows) {
    let person = bySubject.get(row.subject);
    if (person === undefined) {
      person = { email: null, name: null, homeTenantId: null, homeTenantName: null, roles: [] };
      bySubject.set(row.subject, person);
    }
    person.email ??= row.email ?? null;
    person.name ??= row.name ?? null;
    // The PAIR, from the first row that names a home tenant — see the doc comment above for why these two
    // are not taken first-non-null independently of each other.
    if (person.homeTenantName === null && (row.homeTenantName ?? null) !== null) {
      person.homeTenantId = row.homeTenantId ?? null;
      person.homeTenantName = row.homeTenantName ?? null;
    }
    if (!person.roles.some((held) => held.role === row.role)) {
      person.roles.push({ role: row.role, carrier: row.carrier });
    }
  }

  return [...bySubject].map(([subject, person]) => ({
    subject,
    email: person.email,
    name: person.name,
    homeTenantId: person.homeTenantId,
    homeTenantName: person.homeTenantName,
    label: holderLabel({ subject, email: person.email, name: person.name }),
    roles: person.roles,
  }));
}

/**
 * Whether this person's row says where they come from — their HOME ORGANISATION (ADR-0021 §8.2.1).
 *
 * TWO CONDITIONS, and each rules out a row that would otherwise draw a useless line:
 *
 *   * the home tenant must be NAMED. Null is the ordinary answer for the client-role carrier (the home
 *     tenant is the tenant being viewed), for a membership holder the realm would not answer for, and for
 *     an account bound to nothing — and `groupHolders` also leaves it null for an id this product has no
 *     tenant row for, because the line shows an organisation and a bare uuid is not one.
 *   * it must DIFFER from the tenant on screen. A customer's own person has their home organisation in
 *     the page's heading already, so repeating it on every row would be noise on the one list this line
 *     exists to make readable.
 *
 * COMPARED BY ID AND NOT BY NAME, which is the only real judgement call here: a tenant's display name is
 * `TenantSettings.DisplayName`, which that tenant's own pääkäyttäjä edits, so two tenants can legitimately
 * share one and a rename could silently start or stop the line. The id is the account's binding as the
 * realm holds it, against the id of the tenant the console is looking at.
 */
export function showsHomeOrganisation(
  person: Pick<RoleHolderPerson, "homeTenantId" | "homeTenantName">,
  viewedTenantId: string,
): boolean {
  return person.homeTenantName !== null && person.homeTenantId !== viewedTenantId;
}

/**
 * WHICH BOUND A COMPLETED GRANT'S BANNER MAY CLAIM, as a translation key — the carrier decides it, and the
 * two answers are not a nuance.
 *
 * A `tenant_memberships` row is read on EVERY request with no cache (ADR-0021 §4.3), so a membership grant
 * is live at the grantee's next request: a reload is enough and the person never has to leave the session
 * they are in. A client role is a claim on the bearer token (`TenantResolutionMiddleware` reads
 * `clientRoles` off it), so it is honoured only once the token is replaced, which is the next sign-in. That
 * older sentence is still the true one for a client role and `doneGranted` keeps it.
 *
 * `carriers` is the carrier of every grant that ANSWERED, and the membership sentence is claimed only when
 * all of them say membership. The carrier is a property of the ACCOUNT — its `tenant_id` against this
 * tenant — so one submission of one address is one carrier and a mixed answer should not arise; if one ever
 * does, the token-bound sentence is the one that is not wrong about anybody's session. An EMPTY list is the
 * same case rather than a separate one: a run whose every call failed with the declared audit-incomplete
 * 500 can have roles in effect with no response body to read a carrier out of, and this screen then
 * defends the bound it can still establish instead of guessing the stronger one.
 */
export function grantedBannerKey(carriers: readonly string[]): string {
  return carriers.length > 0 && carriers.every((carrier) => carrier === MEMBERSHIP_CARRIER)
    ? "tenants.roles.doneGrantedMembership"
    : "tenants.roles.doneGranted";
}

/**
 * The same for a completed removal, where the carrier is never in doubt: the response says which one the
 * service took off (`TenantRoleGrantDto.holder.carrier`), and the service read it from the
 * `tenant_memberships` row or, finding none, from the realm (ADR-0018 §7). So this is the carrier of the
 * write that just happened and not of a list read before the click — see `PendingRevoke`.
 *
 * The membership removal is the one the single sentence got wrong in the product's favour. The row is gone
 * and the next request reads nothing, so the access has ALREADY ended — a support desk told "access ends at
 * their next sign-in" answers "they can still get in for now", which is the opposite of what happened. A
 * client role really does last until the token is replaced, and `doneRevoked` keeps saying so.
 */
export function revokedBannerKey(carrier: string): string {
  return carrier === MEMBERSHIP_CARRIER
    ? "tenants.roles.doneRevokedMembership"
    : "tenants.roles.doneRevoked";
}

/**
 * The rows a search box's text leaves on screen: the people whose NAME, ADDRESS or SUBJECT contains it,
 * case-insensitively. Empty or blank text filters nothing.
 *
 * WHY THE SUBJECT IS MATCHED and the task asked only for name and e-mail. A row whose realm name could not
 * be read renders by subject alone ({@link holderLabel}), and that string is then the only thing on screen
 * identifying the person — a filter that could not match what the operator can see would hide a row they
 * are looking straight at. It costs nothing: a subject is a uuid, so it matches no ordinary search term.
 *
 * WHY THE ROLE IS NOT MATCHED. The chips are the whole of a row's role content and §3.3 has five codes, so
 * a role search would narrow a list that is already readable at a glance, and it would do it with a second
 * vocabulary (a code, or its label, in whichever language the console is in). The one question this box
 * answers is "where is this person", which is the question 50–100+ customers per consultant creates.
 *
 * Case folding is `toLocaleLowerCase`, not `toLowerCase`: the names are Finnish and the addresses are
 * typed by a Finnish operator, and "Ä" is the letter this choice is about.
 */
export function filterHolders(
  people: readonly RoleHolderPerson[],
  query: string,
): readonly RoleHolderPerson[] {
  const needle = query.trim().toLocaleLowerCase();
  if (needle === "") return people;
  return people.filter((person) =>
    [person.name, person.email, person.subject].some(
      (field) => field !== null && field.toLocaleLowerCase().includes(needle),
    ),
  );
}
